import { useEffect, useRef, useState } from 'react';
import { splitWords, wordsPerTick } from './chat-format';

const TICK_MS = 28;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * The answer typed out word by word, as the design shows it. The whole text
 * arrives at once (the provider documents no token streaming — ADR 0001), so
 * this is presentation only. With reduced motion asked for, or `animate` off,
 * the text is simply there. `onDone` fires once the last word is shown.
 */
export function useTypewriter(
  text: string,
  animate: boolean,
  onDone: () => void,
): { shown: string; typing: boolean } {
  const [words] = useState(() => splitWords(text));
  const [instant] = useState(() => !animate || prefersReducedMotion());
  const [count, setCount] = useState(instant ? words.length : 0);
  const done = useRef(onDone);
  useEffect(() => {
    done.current = onDone;
  }, [onDone]);

  useEffect(() => {
    if (count < words.length) {
      const timer = setTimeout(() => setCount((c) => c + wordsPerTick(words.length)), TICK_MS);
      return () => clearTimeout(timer);
    }
    if (animate) done.current();
    return undefined;
  }, [animate, count, words.length]);

  if (instant) return { shown: text, typing: false };
  const typing = count < words.length;
  return { shown: typing ? words.slice(0, count).join('') : text, typing };
}
